/* eslint-disable class-methods-use-this */
import { Box, TableSortLabel } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch/Switch";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ErrorIcon from "@material-ui/icons/Error";
import ImageIcon from "@material-ui/icons/Image";
import LinkIcon from "@material-ui/icons/Link";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import SpeakerNotesIcon from "@material-ui/icons/SpeakerNotes";
import "handsontable/dist/handsontable.full.css";
import React from "react";
import { TestCaseSearchResult } from "../../../../../api/models/test-series/TestCaseSearchResult";
import Api from "../../../../../api/services/Api";
import AttachmentApi from "../../../../../api/services/AttachmentApi";
import SortingService from "../../../../../api/services/common/SortingService";
import { TestSeriesPermissions } from "../../../../../api/services/PermissionApi";
import TestRequestApi from "../../../../../api/services/TestRequestApi";
import TestSeriesApi from "../../../../../api/services/TestSeriesApi";
import CustomButton from "../../../../../components/Button/CustomButton";
import BlackChipTooltip from "../../../../../components/Chip/BlackChipTooltip";
import VerticalAlignedChip from "../../../../../components/Chip/VerticalAlignedChip";
import WhiteChipTooltip from "../../../../../components/Chip/WhiteChipTooltip";
import AttachmentDetailDialog from "../../../../../components/Dialog/AttachmentDialog/AttachmentDetailDialog";
import {
  ConfirmationDialogContext,
  ConfirmationDialogContextType,
} from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import AttachmentDialog from "../../../../../components/Gallery/AttachmentDialog";
import Attachment from "../../../../../components/Gallery/model/Attachment.model";
import FileReference from "../../../../../components/Gallery/model/FileReference.model";
import { DOCUMENT, OTHER, PICTURE } from "../../../../../components/Gallery/model/GalleryFlag.model";
import LoadedFile from "../../../../../components/Gallery/model/LoadedFile.model";
import StyledTooltip from "../../../../../components/Wrapper/StyledTooltip";
import { ErrorSnackbarContext, ErrorSnackbarContextType } from "../../../../../contexts/ErrorSnackbarContext";
import { TestSeriesContext } from "../../../../../contexts/TestSeriesContext";
import AttachmentFileHandler from "../../../../../helper/AttachmentFileHandler";
import FileNameHelper from "../../../../../helper/FileNameHelper";
import extractValueAndUnitCode from "../../../../../helper/ParameterValueExtractor";
import UploadHelper from "../../../../../helper/UploadHelper";
import { tesaBlue } from "../../../../../styles/colors";
import { RATING_DICTIONARY } from "../../../models/TestCaseRating.model";
import ValidatableTestCase from "../../../models/ValidatableTestCase.model";
import TestSeriesDialogContext, { TestSeriesDialogContextType } from "../TestSeriesTabbedDialogContext";
import TestCasesParametersTable, {
  TestCasesParametersTableRef,
} from "./components/TestCasesParametersTable/TestCasesParametersTable";
import ProgressBar from "./ProgressBar";
import TestCaseRatingIcon from "./TestCaseRatingIcon";
import TestCasesNotesAddDialog from "./TestCasesNotesAddDialog";
import TestCasesNotesDetailDialog from "./TestCasesNotesDetailDialog";
import TestSeriesRegainMenu from "./TestSeriesRegainMenu";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../../../../../components/Dialog/ConfidentialConfirmationDialog";
import generateHeaderRow from "./utils/HeaderUtil";
import { RowData } from "./interfaces/RowData";
import TestCasesOverviewTableFilterLabel from "../../../../../components/TableColumnFilter/TableColumnFilter";
import { filterRows, getFilterOptions } from "./utils/RowDataFilter";
import dataToRows from "./utils/RowDataUtil";
import TestCasesOverviewTableStyle from "./TestCasesOverviewTableStyle";
import TestCasesOverviewTableState from "./interfaces/TestCasesOverviewTableState";
import { FilterableColumn } from "./interfaces/FilterableColumn";
import ParameterValue from "../../../../../api/models/test-series/ParameterValue";
import TestSeriesDTO from "../../../../../api/models/TestSeriesDTO";
import ActionValue from "../../../../../api/models/test-series/ActionValue";
import { setActiveFilter } from "./utils/ActiveFilterLocalStorage";
import MatrixConfigurationDialog from "./components/MatrixConfigurationDialog/MatrixConfigurationDialog";
import MatrixInputDialog from "./components/MatrixInput/MatrixInput";
import getHistoricalButtonText from "../../utils/HistoricalButtonLabel";
import AnalyzeHistoricalResultsDialog from "../../AnalyzeHistoricalResultsDialog";
import CustomProgress from "../../../../../components/Alert/CustomProgress";
import TestInformationApi from "../../../../../api/services/TestInformationApi";
import { AnalyzeHistoricalFormType } from "../../AnalyzeHistoricalForm";

const KEY_DOWN_EVENT = "keydown";
const CAPTURED_BEFORE_CHILDREN = true;

function groupTestCasesById(testCases: TestCaseSearchResult[]): Record<string, TestCaseSearchResult> {
  return testCases.reduce((byId, currTC) => {
    byId[String(currTC.id)] = currTC;
    return byId;
  }, {} as Record<string, TestCaseSearchResult>);
}

const sortingService: SortingService = new SortingService();

interface TestCasesOverviewTableProps {
  testSeriesObject: TestSeriesDTO;
  testCases: ValidatableTestCase[];
  testRequestId: string;
  onClose: () => void;
  startExecution: (id: number) => Promise<void>;
  checkComplete: (id: number) => Promise<boolean>;
  completeExecution: (id: number, ignoreUnratedTestCases: boolean) => void;
  fetchTestSeriesApiData: () => void;
  refreshTestCases: () => void;
  historicalSearchSettings: AnalyzeHistoricalFormType | null;
}

export interface TestCasesOverviewTableRef {
  isDirty: () => boolean;
  save: () => Promise<void>;
}

interface TestCasesOverviewTableInternalProps
  extends TestCasesOverviewTableProps,
    TestSeriesDialogContextType,
    ConfirmationDialogContextType,
    ErrorSnackbarContextType {
  testSeriesPermissions: TestSeriesPermissions;
  classes: Record<string, string>;
}

const TestCasesOverviewTable = TestCasesOverviewTableStyle(
  class
    extends React.Component<TestCasesOverviewTableInternalProps, TestCasesOverviewTableState>
    implements TestCasesOverviewTableRef
  {
    private testCasesCopy = [...this.props.testCases];

    private testCasesById: Record<string, TestCaseSearchResult> = groupTestCasesById(this.props.testCases);

    private testCasesParametersTableRef: TestCasesParametersTableRef | null = null;

    private oldExcludedState: { excluded: boolean; id: number }[] = [];

    private api = new Api(this.props);

    private testSeriesApi = new TestSeriesApi(this.api);

    private testRequestApi = new TestRequestApi(this.api);

    private attachmentApi = new AttachmentApi(this.api);

    private testInformationApi = new TestInformationApi(this.api);

    constructor(props: TestCasesOverviewTableInternalProps) {
      super(props);
      this.testCasesCopy = [...props.testCases];
      this.testCasesById = props.testCases.reduce((byId, currTC) => {
        byId[String(currTC.id)] = currTC;
        return byId;
      }, {} as Record<string, TestCaseSearchResult>);
      this.saveExcludedState();

      this.state = {
        anchorEl: null,
        caseIndexesWithAttachment: [],
        currentTestCaseId: null,
        currentTestCaseFunctionalId: null,
        cursorState: 0,
        headerStateArray: [],
        images: [],
        includedRows: [],
        isMatrixInputDialogOpen: false,
        isMatrixConfigurationDialogOpen: false,
        isNotesAddDialogOpen: false,
        isAttachmentsAddDialogOpen: false,
        isNotesDetailDialogOpen: false,
        isAttachmentsDetailDialogOpen: false,
        isTestCaseTableOpen: false,
        loading: false,
        order: "asc",
        orderBy: "product",
        rows: [],
        selectedTestCaseId: "",
        activeFilters: {
          id: [],
          firstSubstrate: [],
          secondSubstrate: [],
          product: [],
          progress: [],
          buildingAction: [],
          condition: [],
          treatment: [],
          passed: [],
        },
        filterOptions: {
          id: [],
          firstSubstrate: [],
          secondSubstrate: [],
          product: [],
          progress: [],
          buildingAction: [],
          condition: [],
          treatment: [],
          passed: [],
        },
        productSamples: [],
        substrateSamples: [],
        isAnalyzeHistoricalResultsDialogOpen: false,
        highlyConfidentialRegex: null,
        showConfidentialityDialog: false,
        pendingAttachmentUpload: null,
      };

      this.props.fetchTestSeriesApiData.bind(this);

      this.setState = this.setState.bind(this);
      this.toggleSelectAllColumns = this.toggleSelectAllColumns.bind(this);
      this.handleOpenNotesAddDialog = this.handleOpenNotesAddDialog.bind(this);
      this.handleOpenAttachmentsAddDialog = this.handleOpenAttachmentsAddDialog.bind(this);
      this.handleOpenNotesDetailDialog = this.handleOpenNotesDetailDialog.bind(this);
      this.handleOpenAttachmentsDetailDialog = this.handleOpenAttachmentsDetailDialog.bind(this);
      this.handleResetActiveFilter = this.handleResetActiveFilter.bind(this);
    }

    componentDidMount() {
      this.updateTable();
      this.refreshFilterOptions();

      window.addEventListener(KEY_DOWN_EVENT, this.handleKeyDown, CAPTURED_BEFORE_CHILDREN);

      fetchHighlyConfidentialRegex().then((regex) => {
        this.setState({ highlyConfidentialRegex: regex });
      });
    }

    componentDidUpdate(prevProps: TestCasesOverviewTableProps, prevState: TestCasesOverviewTableState) {
      const { testSeriesObject, testCases } = this.props;
      const { cursorState, activeFilters } = this.state;

      if (prevState.cursorState !== cursorState) {
        this.removeErrorHighlight(cursorState);
      }

      if (testSeriesObject !== prevProps.testSeriesObject || testCases !== prevProps.testCases) {
        this.updateTable();
        this.refreshFilterOptions();
      }

      if (testCases !== prevProps.testCases) {
        this.testCasesCopy = [...testCases];
        this.testCasesById = groupTestCasesById(testCases);
      }

      if (activeFilters !== prevState.activeFilters) {
        setActiveFilter(testSeriesObject.id, activeFilters);
        this.generateSubstrateAndProductRows(testCases);
        this.updateCursorState(0);
      }
    }

    componentWillUnmount() {
      window.removeEventListener(KEY_DOWN_EVENT, this.handleKeyDown, CAPTURED_BEFORE_CHILDREN);
    }

    private fileName = (file: LoadedFile): string => {
      const index = this.state.images.map((image) => image.id).indexOf(file.id);
      return FileNameHelper.postfixFileBeforeEnding(index < 0 ? "" : `-${index + 1}`, (file as LoadedFile).name);
    };

    private attachmentFileHandler = new AttachmentFileHandler(
      this.attachmentApi,
      {
        remove: (idToBeDeleted) => {
          const { testSeriesObject } = this.props;
          return this.testSeriesApi
            .removeAttachmentFromTestCase(this.state.currentTestCaseId as number, testSeriesObject.id!, idToBeDeleted)
            .then((removeRes) => {
              return this.testSeriesApi
                .fetchAttachmentsToTestCase(this.state.currentTestCaseId as number, testSeriesObject.id!)
                .then((fetchRes) => ({
                  idToBeDeleted: removeRes.data,
                  attachments: fetchRes.data.map((uuid) => ({
                    uuid,
                    editable: "READ_WRITE",
                  })),
                }));
            });
        },
        append: () => {
          throw new Error("Append is not implemented for test cases");
        },
        zipFileName: (files: FileReference[]): string => {
          if (files.length === 1) {
            const file = files[0];
            if (file.status === "LOADED") {
              return this.fileName(file as LoadedFile);
            }
            return file.id;
          }
          return `LEAP.zip`;
        },
        fileName: this.fileName,
      },
      () => {
        return this.testRequestApi
          .getTestRequestAttachmentClearanceToken(this.props.testRequestId)
          .then((res) => res.data);
      },
    );

    private handleOpenNotesAddDialog(): void {
      const { fetchTestSeriesApiData } = this.props;
      this.setState((prevState) => {
        if (prevState.isNotesAddDialogOpen) {
          fetchTestSeriesApiData();
        }
        return {
          isNotesAddDialogOpen: !prevState.isNotesAddDialogOpen,
        };
      });
    }

    private handleOpenAttachmentsAddDialog(): void {
      const { fetchTestSeriesApiData } = this.props;
      this.setState((prevState) => {
        if (prevState.isAttachmentsAddDialogOpen) {
          fetchTestSeriesApiData();
        }
        return {
          isAttachmentsAddDialogOpen: !prevState.isAttachmentsAddDialogOpen,
        };
      });
    }

    private handleOpenNotesDetailDialog(testCaseId: string | number): void {
      const { fetchTestSeriesApiData } = this.props;
      this.setState((prevState) => {
        if (prevState.isNotesDetailDialogOpen) {
          fetchTestSeriesApiData();
        }
        return {
          ...prevState,
          isNotesDetailDialogOpen: !prevState.isNotesDetailDialogOpen,
          selectedTestCaseId: prevState.selectedTestCaseId === "" ? testCaseId : "",
        };
      });
    }

    private handleOpenAttachmentsDetailDialog(testCaseId: string): void {
      const { testSeriesObject } = this.props;
      const currentTestCaseIndexNumber = this.state.rows.filter((testCase) => String(testCase.id) === testCaseId)[0]
        ?.indexNumber;
      const currentTestCaseFunctionalId = `${testSeriesObject.indexNumber}-${currentTestCaseIndexNumber || ""}`;

      this.setState((prevState) => {
        if (prevState.isAttachmentsDetailDialogOpen) {
          // Close dialog, clear testCase specific state
          this.setState({ images: [], currentTestCaseFunctionalId: null });
        } else {
          // Open dialog, fetch attachment data, populate testCase specific state
          this.testSeriesApi
            .fetchAttachmentsToTestCase(parseInt(testCaseId, 10), testSeriesObject.id!)
            .then((res) => {
              const attachments: Attachment[] = res.data.map((uuid) => ({
                uuid,
                editable: "READ_WRITE",
              }));
              return this.attachmentFileHandler.load(attachments);
            })
            .then((images) => {
              this.setState({
                images,
                currentTestCaseFunctionalId,
                currentTestCaseId: parseInt(testCaseId, 10),
              });
            })
            .catch(this.props.handleHttpError);
        }
        return {
          ...prevState,
          isAttachmentsDetailDialogOpen: !prevState.isAttachmentsDetailDialogOpen,
          selectedTestCaseId: prevState.selectedTestCaseId === "" ? testCaseId : "",
        };
      });
    }

    private handleAddSeriesNoteToCase(indexNumber: number): void {
      const { caseIndexesWithAttachment } = this.state;

      if (caseIndexesWithAttachment.includes(indexNumber)) {
        const newState = caseIndexesWithAttachment.filter((arrIndex) => {
          return arrIndex !== indexNumber;
        });

        this.setState((prevState) => {
          return {
            ...prevState,
            caseIndexesWithAttachment: newState,
          };
        });
      } else {
        this.setState((prevState) => {
          return {
            ...prevState,
            caseIndexesWithAttachment: [...prevState.caseIndexesWithAttachment, indexNumber],
          };
        });
      }
    }

    private onlyIfValid(callback: () => void) {
      this.testCasesParametersTableRef
        ?.saveCurrent()
        .then(callback)
        .catch(() => {});
    }

    private setIndexValid = (index: number): void => {
      const { rows } = this.state;

      this.setState({
        rows: rows.map((r, ri) => ({
          ...r,
          invalid: ri === index ? false : r.invalid,
        })),
      });
    };

    private handleClose = () => {
      this.setState({
        anchorEl: null,
      });
    };

    private handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      this.setState({
        anchorEl: event.currentTarget,
      });
    };

    private showCompleteExecutionDialog = (hasUnratedTestCases: boolean, onConfirm: () => void): void => {
      const { showConfirmationDialog, handleDialogNegativeCallback } = this.props;
      const dialogParams = hasUnratedTestCases
        ? {
            headline: "Complete Execution with unrated test cases",
            text: (
              <>
                <p>Not all test cases are rated.</p>
                <p>From now on test results cannot be edited.</p>
              </>
            ),
            confirmColor: tesaBlue,
            confirmText: "Complete with unrated test cases",
          }
        : {
            headline: "Complete Execution",
            text: "From now on test results cannot be edited.",
            confirmColor: tesaBlue,
            confirmText: "Complete",
          };
      showConfirmationDialog(dialogParams).then(onConfirm).catch(handleDialogNegativeCallback);
    };

    private handleLeftButtonClick = (): void => {
      const { cursorState } = this.state;
      const rowCount = this.rowRenderArray().length;
      this.onlyIfValid(() => {
        if (cursorState === 0) {
          this.updateCursorState(rowCount - 1);
        } else if (cursorState <= rowCount) {
          this.setState((prevState) => {
            return {
              cursorState: (prevState.cursorState - 1) % rowCount,
            };
          });
        }
      });
    };

    private handleRightButtonClick = (): void => {
      const { cursorState } = this.state;
      const rowCount = this.rowRenderArray().length;
      this.onlyIfValid(() => {
        if (cursorState < rowCount) {
          this.setState((prevState) => {
            return {
              cursorState: (prevState.cursorState + 1) % rowCount,
            };
          });
        } else if (cursorState >= rowCount) {
          this.updateCursorState(0);
        }
      });
    };

    private handleKeyDown = (e: KeyboardEvent) => {
      // Arrow up/down button should select next/previous list element
      // KeyCode 65 is letter "v"
      if (e.keyCode === 86 && e.altKey) {
        this.handleLeftButtonClick();
      } else if (e.keyCode === 66 && e.altKey) {
        // KeyCode 83 is letter "b"
        this.handleRightButtonClick();
      }
    };

    private saveExcludedState(): void {
      this.oldExcludedState = this.calcExcludedState();
    }

    private toggleSelectAllColumns(): void {
      const rowCount = this.rowRenderArray().length;

      this.setState((prevState) => {
        return {
          ...prevState,
          caseIndexesWithAttachment:
            prevState.caseIndexesWithAttachment.length < rowCount
              ? this.rowRenderArray().map((row) => row.indexNumber)
              : [],
        };
      });
    }

    private calcExcludedState(): { excluded: boolean; id: number }[] {
      return this.testCasesCopy
        .map((testCase) => ({
          excluded: testCase.excluded,
          id: testCase.id,
        }))
        .sort((a, b) => a.id - b.id);
    }

    private rowRenderArray(): RowData[] {
      const { rows, includedRows } = this.state;
      const { showExcludedTests } = this.props;
      return showExcludedTests ? rows : includedRows;
    }

    private extractIncludedExcludedTestCases(): {
      includedRequestBody: { excluded: boolean; testCaseIds: number[] };
      excludedRequestBody: { excluded: boolean; testCaseIds: number[] };
    } {
      const includedTestCaseIds = [] as number[];
      const excludedTestCaseIds = [] as number[];
      this.testCasesCopy.forEach((testCase: ValidatableTestCase) => {
        if (testCase.excluded) {
          excludedTestCaseIds.push(testCase.id);
        } else {
          includedTestCaseIds.push(testCase.id);
        }
      });
      const excludedRequestBody = {
        excluded: true,
        testCaseIds: excludedTestCaseIds,
      };
      const includedRequestBody = {
        excluded: false,
        testCaseIds: includedTestCaseIds,
      };

      return { includedRequestBody, excludedRequestBody };
    }

    private generateHeaderRow(testCasesArr: TestCaseSearchResult[]) {
      this.setState({
        headerStateArray: generateHeaderRow(testCasesArr),
      });
    }

    private generateSubstrateAndProductRows(testCasesArr: ValidatableTestCase[]) {
      const { order, orderBy, activeFilters } = this.state;
      const { testSeriesObject } = this.props;

      const newRows = dataToRows(testCasesArr, testSeriesObject);
      const filteredRows = filterRows(newRows, activeFilters, testSeriesObject.indexNumber!);
      const sortedAndFilteredRows = sortingService.stableSort(
        filteredRows,
        sortingService.getComparator(order, orderBy),
      );
      const sortedAndFilteredIncludedRows = sortingService.stableSort(
        filteredRows.filter((row) => row.included === true),
        sortingService.getComparator(order, orderBy),
      );
      this.setState({
        rows: sortedAndFilteredRows,
        includedRows: sortedAndFilteredIncludedRows,
      });
    }

    private handleCheckboxChange(row: RowData) {
      const toggledTestCase = this.testCasesCopy.find((e) => e.id === row.id);
      if (toggledTestCase) {
        toggledTestCase.excluded = !toggledTestCase.excluded;
        // These rows generated below will be in the handsontable
        this.generateSubstrateAndProductRows(this.testCasesCopy);
      }
    }

    private updateTable() {
      const { testCases, testSeriesObject, setShowExcludedTests } = this.props;
      if (testSeriesObject.testSeriesStatus === "IN_EXECUTION") {
        setShowExcludedTests(false);
      }
      this.generateSubstrateAndProductRows(testCases);
      this.generateHeaderRow(testCases);
    }

    private refreshFilterOptions() {
      const { testSeriesObject, testCases } = this.props;
      this.setState((previousState) => {
        return {
          ...previousState,
          filterOptions: getFilterOptions(testCases, testSeriesObject.indexNumber!),
        };
      });
    }

    private updateCursorState(newCursorStateValue: number) {
      this.setState((prevState) => {
        if (newCursorStateValue !== prevState.cursorState) {
          this.setIndexValid(newCursorStateValue);
        }

        return {
          ...prevState,
          cursorState: newCursorStateValue !== prevState.cursorState ? newCursorStateValue : prevState.cursorState,
        };
      });
    }

    private openTestCase(i: number): void {
      this.setState({
        isTestCaseTableOpen: true,
      });
      this.updateCursorState(i);
      this.removeErrorHighlight(i);
    }

    private showExecutionDialog(onConfirm: () => void): void {
      const { showConfirmationDialog } = this.props;

      showConfirmationDialog({
        headline: `Start Execution`,
        text: "From now on changes in the configurator and tests table are disabled.",
        confirmText: "Continue",
      })
        .then(onConfirm)
        .catch(() => {});
    }

    private handleSave(): Promise<void> {
      const testRequestApi = new TestRequestApi(new Api(this.props));
      const { includedRequestBody, excludedRequestBody } = this.extractIncludedExcludedTestCases();
      const { testSeriesObject } = this.props;
      if (!this.isDirty()) {
        return Promise.resolve();
      }
      this.setState({ loading: true });
      return testRequestApi
        .updateTestCasesExclusionById(testSeriesObject.id!, includedRequestBody)
        .then(() => {
          return testRequestApi.updateTestCasesExclusionById(testSeriesObject.id!, excludedRequestBody);
        })
        .then(() => {
          this.saveExcludedState();
          this.setState({ loading: false });
        })
        .catch((error) => {
          this.setState({ loading: false });
          throw error;
        });
    }

    private removeErrorHighlight(i: number): void {
      const { includedRows } = this.state;
      const rowsCopy = includedRows.slice();

      if (rowsCopy[i]) {
        rowsCopy[i].invalid = false;
      }

      this.setState({ includedRows: rowsCopy });
    }

    private renderWhiteChip(actionValue: ActionValue, cssClassName: string) {
      return (
        <WhiteChipTooltip
          key={actionValue.id || (() => JSON.stringify(actionValue))()}
          values={actionValue
            .parameterValues!.filter((parameterValue) => !parameterValue.uncontrolled)
            .map((parameterValue) => {
              const valueAndUnitCode = extractValueAndUnitCode(parameterValue as ParameterValue);
              return {
                label: parameterValue.parameter.name,
                value: valueAndUnitCode.value,
                unitCode: valueAndUnitCode.unitCode,
              };
            })}
        >
          <VerticalAlignedChip className={cssClassName} label={actionValue?.action?.name} variant="outlined" />
        </WhiteChipTooltip>
      );
    }

    private handleSetActiveFilter(filter: string[], filterAttribute: FilterableColumn) {
      this.setState((prevState) => {
        return {
          ...prevState,
          activeFilters: {
            ...prevState.activeFilters,
            [filterAttribute]: filter,
          },
        };
      });
    }

    private handleResetActiveFilter() {
      this.setState((prevState) => {
        return {
          ...prevState,
          activeFilters: {
            id: [],
            firstSubstrate: [],
            secondSubstrate: [],
            product: [],
            progress: [],
            buildingAction: [],
            condition: [],
            treatment: [],
            passed: [],
          },
        };
      });
    }

    public isDirty(): boolean {
      return JSON.stringify(this.oldExcludedState) !== JSON.stringify(this.calcExcludedState());
    }

    public save(): Promise<void> {
      return this.handleSave();
    }

    public renderMultiCaseHeader(attachmentType: string): string {
      const { testSeriesObject } = this.props;
      const renderCaseIds = (): string[] => {
        return this.state.caseIndexesWithAttachment.map((val, index) => {
          if (index === 0) {
            return `${testSeriesObject.indexNumber}-${val}`;
          }
          return `/${testSeriesObject.indexNumber}-${val}`;
        });
      };
      return `UPLOAD ${attachmentType} TO TEST ID ${renderCaseIds().join("")}`;
    }

    private handleAnalyzeHistoricalResultsDialogOpen = (isOpen: boolean) => {
      this.setState((prevState) => {
        return { ...prevState, isAnalyzeHistoricalResultsDialogOpen: isOpen };
      });
    };

    private handleMatrixConfigurationDialogOpen = (value: boolean) => {
      const openedFromInputMatrix = localStorage.getItem("openedConfig");
      if (openedFromInputMatrix && JSON.parse(openedFromInputMatrix)) {
        localStorage.setItem("openedConfig", JSON.stringify(false));
        this.setState((prevState) => ({
          ...prevState,
          isMatrixConfigurationDialogOpen: false,
          isMatrixInputDialogOpen: true,
        }));
      } else {
        this.setState((prevState) => ({ ...prevState, isMatrixConfigurationDialogOpen: value }));
      }
    };

    private handleMatrixInputDialogOpen = (value: boolean) => {
      this.setState((prevState) => ({ ...prevState, isMatrixInputDialogOpen: value }));
      this.props.refreshTestCases();
    };

    private executeTestSeries = async () => {
      const { startExecution, testSeriesObject } = this.props;

      const testInformationResponse = await this.testInformationApi.fetchTestInformation(this.props.testRequestId);

      const testSeriesToExecute = testInformationResponse.data.testPlans.find(({ testSeriesSearchResults }) => {
        return testSeriesSearchResults.some(({ id, state }) => id === testSeriesObject.id && state === "DRAFT");
      });

      if (testSeriesToExecute) {
        this.showExecutionDialog(() => {
          this.handleSave()
            .then(() => startExecution(testSeriesObject.id))
            .then(() => {
              this.setState((prevState) => ({
                ...prevState,
                isMatrixInputDialogOpen: true,
              }));
            });
        });
      } else {
        this.setState((prevState) => ({
          ...prevState,
          isMatrixInputDialogOpen: true,
        }));
      }
    };

    private configMatrix = () => {
      localStorage.setItem("openedConfig", JSON.stringify(true));
      this.setState((prevState) => {
        return { ...prevState, isMatrixConfigurationDialogOpen: true, isMatrixInputDialogOpen: false };
      });
    };

    render() {
      const namingStrategy = {
        nameDetailDialog: (file: LoadedFile) => {
          return {
            header: `GALLERY TEST ID ${this.state.currentTestCaseFunctionalId}`,
            subheader: file.name,
          };
        },
      };

      const {
        classes,
        testSeriesObject,
        testCases,
        onClose,
        startExecution,
        showExcludedTests,
        showSnackbar,
        showErrorSnackbar,
        setShowExcludedTests,
        historicalTestCasesMatchCounter,
        testSeriesPermissions,
        testRequestId,
        historicalSearchSettings,
      } = this.props;
      const {
        anchorEl,
        caseIndexesWithAttachment,
        cursorState,
        headerStateArray,
        includedRows,
        isNotesAddDialogOpen,
        isAttachmentsAddDialogOpen,
        isNotesDetailDialogOpen,
        isAttachmentsDetailDialogOpen,
        isTestCaseTableOpen,
        order,
        orderBy,
        rows,
        loading,
        selectedTestCaseId,
        isAnalyzeHistoricalResultsDialogOpen,
        filterOptions,
        activeFilters,
      } = this.state;
      const executionReadOnly = !testSeriesPermissions.editExecution;

      const showAdditionalColumns =
        testSeriesObject.testSeriesStatus === "IN_EXECUTION" ||
        testSeriesObject.testSeriesStatus === "CANCELED" ||
        testSeriesObject.testSeriesStatus === "COMPLETED";

      if (loading) {
        return <CustomProgress />;
      }

      return (
        <>
          {isAnalyzeHistoricalResultsDialogOpen && (
            <AnalyzeHistoricalResultsDialog
              testMethodName={testSeriesObject.testMethodReference.testMethodName!}
              onClose={() => this.handleAnalyzeHistoricalResultsDialogOpen(false)}
              open={isAnalyzeHistoricalResultsDialogOpen}
              settingsCached={historicalSearchSettings}
            />
          )}
          {this.state.isMatrixConfigurationDialogOpen && (
            <MatrixConfigurationDialog
              testSeries={testSeriesObject}
              open={this.state.isMatrixConfigurationDialogOpen}
              handleDialogOpen={this.handleMatrixConfigurationDialogOpen}
            />
          )}
          {this.state.isMatrixInputDialogOpen && (
            <MatrixInputDialog
              testSeries={testSeriesObject}
              testCaseIds={this.state.includedRows.map((includedRow) => includedRow.id)}
              readOnly={executionReadOnly}
              open={this.state.isMatrixInputDialogOpen}
              handleDialogOpen={this.handleMatrixInputDialogOpen}
              handleOpenConfiguration={this.configMatrix}
            />
          )}
          <Grid container>
            <Grid container justify="space-between">
              <Box marginTop={1}>
                <CustomButton
                  disabled={!historicalTestCasesMatchCounter}
                  text={getHistoricalButtonText(historicalTestCasesMatchCounter)}
                  onClick={() => this.handleAnalyzeHistoricalResultsDialogOpen(true)}
                />
              </Box>
              <Box display="flex" marginBottom={2}>
                {testSeriesPermissions.createLinked && (
                  <Box marginTop={1}>
                    <CustomButton
                      onClick={this.handleMenuClick}
                      startIcon={
                        <LinkIcon
                          style={{
                            height: "1rem",
                            width: "1rem",
                          }}
                        />
                      }
                    />
                  </Box>
                )}
                {testSeriesPermissions.start ? (
                  <Box marginLeft={3} marginTop={1}>
                    <CustomButton
                      fullWidth
                      text="Start Execution"
                      startIcon={
                        <PlayCircleOutlineIcon
                          style={{
                            height: "1rem",
                            width: "1rem",
                            marginRight: "0.5rem",
                          }}
                        />
                      }
                      onClick={() => {
                        this.showExecutionDialog(() => {
                          this.handleSave().then(() => {
                            startExecution(testSeriesObject.id!);
                          });
                        });
                      }}
                    />
                  </Box>
                ) : (
                  testSeriesPermissions.completeExecution && (
                    <Box marginLeft={3} marginTop={1}>
                      <CustomButton
                        fullWidth
                        text="Complete Execution"
                        onClick={() => {
                          const { completeExecution, checkComplete, handleHttpError } = this.props;
                          this.handleResetActiveFilter();
                          checkComplete(testSeriesObject.id!)
                            .then((hasUnratedTestCases) =>
                              this.showCompleteExecutionDialog(hasUnratedTestCases, () => {
                                completeExecution(testSeriesObject.id!, hasUnratedTestCases);
                              }),
                            )
                            .catch(handleHttpError);
                        }}
                      />
                    </Box>
                  )
                )}
                <Box marginLeft={testSeriesPermissions.createLinked ? 3 : 0} marginTop={1}>
                  <CustomButton
                    fullWidth
                    text={testSeriesPermissions.changeTestCaseExclusion ? "Save and Close" : "Close"}
                    onClick={
                      testSeriesPermissions.changeTestCaseExclusion ? () => this.handleSave().then(onClose) : onClose
                    }
                  />
                </Box>
                <Box marginLeft={3} marginTop={1}>
                  <CustomButton
                    fullWidth
                    text="Configure Matrix Input"
                    onClick={() =>
                      this.setState((prevState) => {
                        return { ...prevState, isMatrixConfigurationDialogOpen: true };
                      })
                    }
                  />
                </Box>
                <Box marginLeft={3} marginTop={1}>
                  <CustomButton fullWidth primary text="Open Matrix Input" onClick={this.executeTestSeries} />
                </Box>
              </Box>
            </Grid>
          </Grid>
          <div className={classes.cellHeadline2}>Fixed Actions and Parameters</div>
          <Box pb={2}>
            {testSeriesObject.substrateSections?.[0]?.actionsOnAllSubstrates?.actionValues?.map(
              (actionValue: ActionValue) => this.renderWhiteChip(actionValue, classes.whiteChip),
            ) ?? []}
            {testSeriesObject.substrateSections?.[1]?.actionsOnAllSubstrates?.actionValues?.map(
              (actionValue: ActionValue) => this.renderWhiteChip(actionValue, classes.whiteChip),
            ) ?? []}
            {testSeriesObject.productSection &&
              testSeriesObject.productSection.actionsOnAllProducts?.actionValues?.map((actionValue: ActionValue) =>
                this.renderWhiteChip(actionValue, classes.whiteChip),
              )}
            {testSeriesObject.specimenSection &&
              testSeriesObject.specimenSection.buildingActionsOnAllSpecimen?.actionValues?.map(
                (actionValue: ActionValue) => this.renderWhiteChip(actionValue, classes.whiteChip),
              )}
            {testSeriesObject.specimenSection &&
              testSeriesObject.specimenSection.treatmentOnAllSpecimen?.actionValues?.map((actionValue: ActionValue) =>
                this.renderWhiteChip(actionValue, classes.whiteChip),
              )}
            {testSeriesObject.conditionSection &&
              testSeriesObject.conditionSection.parametersInAllTests?.map((parameterValue: ParameterValue) => {
                const valueAndUnitCode = extractValueAndUnitCode(parameterValue);
                return (
                  <WhiteChipTooltip
                    key={parameterValue.id || (() => JSON.stringify(parameterValue))()}
                    values={[
                      {
                        value: valueAndUnitCode.value,
                        unitCode: valueAndUnitCode.unitCode,
                      },
                    ]}
                  >
                    <VerticalAlignedChip
                      className={classes.whiteChip}
                      label={parameterValue.parameter.name}
                      variant="outlined"
                    />
                  </WhiteChipTooltip>
                );
              })}
          </Box>
          <Grid container item xs={12} justify="flex-end" alignItems="flex-end">
            <CustomButton text="Remove all filters" onClick={this.handleResetActiveFilter} />
          </Grid>
          {showAdditionalColumns && testSeriesPermissions.editResultAttachmentsAndNotes && (
            <Box pb={2} display="flex" justifyContent="flex-end" alignItems="flex-end">
              <Box marginRight="24px">
                <CustomButton
                  noTextWrap
                  disabled={caseIndexesWithAttachment.length === 0}
                  onClick={this.handleOpenAttachmentsAddDialog}
                  text="Add Attachments on Tests"
                  startIcon={
                    <ImageIcon
                      style={{
                        height: "13.4px",
                        marginRight: "9.4px",
                        width: "13.4px",
                      }}
                    />
                  }
                />
              </Box>
              <CustomButton
                noTextWrap
                disabled={caseIndexesWithAttachment.length === 0}
                onClick={this.handleOpenNotesAddDialog}
                text="Add Note on Tests"
                startIcon={
                  <SpeakerNotesIcon
                    style={{
                      height: "13.4px",
                      marginRight: "9.4px",
                      width: "13.4px",
                    }}
                  />
                }
              />
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TableContainer>
                <Table className={classes.table} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {headerStateArray.map((headCell) => {
                        return (
                          <TableCell
                            key={headCell.id}
                            align="left"
                            className={[classes.label].concat(headCell.name === "ID" ? classes.id : "").join(" ")}
                            sortDirection={orderBy === headCell.id ? order : false}
                          >
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={orderBy === headCell.id ? order : "asc"}
                              onClick={() => {
                                this.setState({
                                  rows: sortingService.stableSort(
                                    rows,
                                    sortingService.getComparator(order === "asc" ? "desc" : "asc", headCell.id),
                                  ),
                                  includedRows: sortingService.stableSort(
                                    includedRows,
                                    sortingService.getComparator(order === "asc" ? "desc" : "asc", headCell.id),
                                  ),
                                  orderBy: headCell.id,
                                  order: order === "asc" ? "desc" : "asc",
                                });
                              }}
                            >
                              {headCell.name}
                            </TableSortLabel>
                            {headCell.filterableColumnId && (
                              <TestCasesOverviewTableFilterLabel
                                activeFilters={activeFilters[headCell.filterableColumnId]}
                                filterOptions={filterOptions[headCell.filterableColumnId]}
                                onFilterChange={(filter) => {
                                  this.handleSetActiveFilter(filter, headCell.filterableColumnId as FilterableColumn);
                                }}
                                popoverId={`${headCell.filterableColumnId}-popover`}
                              />
                            )}
                          </TableCell>
                        );
                      })}

                      <TableCell width="70px" className={classes.label} id="passed">
                        Passed?
                        <TestCasesOverviewTableFilterLabel
                          activeFilters={activeFilters.passed}
                          filterOptions={filterOptions.passed}
                          onFilterChange={(filter) => {
                            this.handleSetActiveFilter(filter, "passed");
                          }}
                          popoverId="passed-popover"
                        />
                      </TableCell>

                      <TableCell width="70px" className={classes.label} id="progress">
                        Progress
                        <TestCasesOverviewTableFilterLabel
                          activeFilters={activeFilters.progress}
                          filterOptions={filterOptions.progress}
                          onFilterChange={(filter) => {
                            this.handleSetActiveFilter(filter, "progress");
                          }}
                          popoverId="progress-popover"
                        />
                      </TableCell>

                      {testSeriesObject.testSeriesStatus === "DRAFT" && (
                        <TableCell width="60px" align="left" className={classes.label}>
                          <StyledTooltip title="Included in Test Series">
                            <Box>Included</Box>
                          </StyledTooltip>
                        </TableCell>
                      )}
                      {showAdditionalColumns && (
                        <>
                          <TableCell width="70px" className={[classes.label, classes.checkboxCell].join(" ")}>
                            Note
                          </TableCell>
                          <TableCell width="70px" className={[classes.label, classes.checkboxCell].join(" ")}>
                            Att.
                          </TableCell>
                        </>
                      )}
                      {showAdditionalColumns && testSeriesPermissions.editResultAttachmentsAndNotes && (
                        <TableCell width="60px" className={classes.checkboxCell}>
                          <Checkbox
                            checked={caseIndexesWithAttachment.length === this.rowRenderArray().length}
                            color="secondary"
                            indeterminate
                            onClick={this.toggleSelectAllColumns}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.rowRenderArray().map((row, i) => {
                      const showRow = showExcludedTests || (!showExcludedTests && row.included);

                      const onCellClick = () => {
                        if (testSeriesPermissions.accessExecution) {
                          this.setState({ isTestCaseTableOpen: true });
                        }

                        this.setIndexValid(i);
                        this.setState({ cursorState: i });
                      };

                      if (showRow) {
                        return (
                          <TableRow
                            className={[
                              testSeriesPermissions.accessExecution && classes.cursor,
                              i === cursorState ? classes.highlightedRow : classes.tableRow,
                              row.invalid && classes.tableRowInvalid,
                            ].join(" ")}
                            key={row.id}
                            hover
                          >
                            <TableCell component="td" scope="row" onClick={onCellClick}>
                              <Box display="flex" flexDirection="row">
                                {row.invalid && (
                                  <Box paddingRight={1.5} margin={-0.5}>
                                    <ErrorIcon color="error" fontSize="small" />
                                  </Box>
                                )}
                                <Box component="span" display="flex" alignItems="center">
                                  {`${testSeriesObject.indexNumber}-${row.indexNumber}`}
                                </Box>
                              </Box>
                            </TableCell>

                            {row.firstSubstrate && (
                              <BlackChipTooltip
                                noChip
                                section="substrate"
                                secondaryChips={row.firstSubstrateSecondaryChips || []}
                                noDefaultTitle
                              >
                                <TableCell
                                  component="td"
                                  scope="row"
                                  onClick={() => {
                                    this.openTestCase(i);
                                  }}
                                >
                                  {row.firstSubstrate}
                                </TableCell>
                              </BlackChipTooltip>
                            )}
                            {row.secondSubstrate && (
                              <BlackChipTooltip
                                noChip
                                section="substrate"
                                secondaryChips={row.secondSubstrateSecondaryChips || []}
                                noDefaultTitle
                              >
                                <TableCell
                                  component="td"
                                  scope="row"
                                  onClick={() => {
                                    this.openTestCase(i);
                                  }}
                                >
                                  {row.secondSubstrate}
                                </TableCell>
                              </BlackChipTooltip>
                            )}
                            <BlackChipTooltip
                              noChip
                              section="material"
                              secondaryChips={row.productSampleSecondaryChips || []}
                              subHeader={row.productSubHeader}
                              noDefaultTitle
                            >
                              <TableCell
                                component="td"
                                scope="row"
                                onClick={() => {
                                  this.openTestCase(i);
                                }}
                              >
                                {row.product}
                              </TableCell>
                            </BlackChipTooltip>

                            {row.buildingAction && (
                              <BlackChipTooltip
                                noChip
                                section="testpiece"
                                secondaryChips={row.buildingActionSecondaryChips || []}
                                noDefaultTitle
                              >
                                <TableCell
                                  component="td"
                                  scope="row"
                                  onClick={() => {
                                    this.openTestCase(i);
                                  }}
                                >
                                  {row.buildingAction !== "__NONE__" ? row.buildingAction : ""}
                                </TableCell>
                              </BlackChipTooltip>
                            )}
                            {row.treatment && (
                              <BlackChipTooltip
                                noChip
                                section="testpiece"
                                secondaryChips={row.specimenTreatmentSecondaryChips || []}
                                noDefaultTitle
                              >
                                <TableCell
                                  component="td"
                                  scope="row"
                                  onClick={() => {
                                    this.openTestCase(i);
                                  }}
                                >
                                  {row.treatment !== "__NONE__" ? row.treatment : ""}
                                </TableCell>
                              </BlackChipTooltip>
                            )}
                            {row.condition && (
                              <BlackChipTooltip
                                noChip
                                section="condition"
                                secondaryChips={row.conditionSecondaryChips || []}
                                noDefaultTitle
                              >
                                <TableCell
                                  component="td"
                                  scope="row"
                                  onClick={() => {
                                    this.openTestCase(i);
                                  }}
                                >
                                  {row.condition !== "__NONE__" ? row.condition : ""}
                                </TableCell>
                              </BlackChipTooltip>
                            )}
                            <StyledTooltip title={RATING_DICTIONARY[row.rating] || ""}>
                              <TableCell component="td" scope="row">
                                <TestCaseRatingIcon rating={row.rating} />
                              </TableCell>
                            </StyledTooltip>
                            <TableCell component="td" scope="row">
                              <ProgressBar completed={row.progress?.completed} total={row.progress?.total} />
                            </TableCell>
                            {testSeriesObject.testSeriesStatus === "DRAFT" && (
                              <TableCell component="td" scope="row" className={classes.checkboxCell}>
                                <Checkbox
                                  className={classes.testCasesCheckBox}
                                  disabled={!testSeriesPermissions.changeTestCaseExclusion}
                                  checked={row.included}
                                  onChange={() => {
                                    this.handleCheckboxChange(row);
                                  }}
                                />
                              </TableCell>
                            )}
                            {showAdditionalColumns && (
                              <>
                                <TableCell component="td" scope="row" className={classes.checkboxCell}>
                                  {row.hasNotes && (
                                    <StyledTooltip title="View Notes">
                                      <SpeakerNotesIcon
                                        className={classes.noteOrAttachmentIcon}
                                        onClick={() => this.handleOpenNotesDetailDialog(row.id)}
                                      />
                                    </StyledTooltip>
                                  )}
                                </TableCell>
                                <TableCell component="td" scope="row" className={classes.checkboxCell}>
                                  {row.hasAttachments && (
                                    <StyledTooltip title="View Attachments">
                                      <ImageIcon
                                        className={classes.noteOrAttachmentIcon}
                                        onClick={() => this.handleOpenAttachmentsDetailDialog(String(row.id))}
                                      />
                                    </StyledTooltip>
                                  )}
                                </TableCell>
                                {testSeriesPermissions.editResultAttachmentsAndNotes && (
                                  <TableCell component="td" scope="row" className={classes.checkboxCell}>
                                    <Checkbox
                                      className={classes.testCasesCheckBox}
                                      checked={caseIndexesWithAttachment.includes(row.indexNumber)}
                                      color="secondary"
                                      onChange={() => {
                                        this.handleAddSeriesNoteToCase(row.indexNumber);
                                      }}
                                    />
                                  </TableCell>
                                )}
                              </>
                            )}
                          </TableRow>
                        );
                      }
                      return null;
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {this.rowRenderArray().length === 0 && (
                <Box marginTop={1}>No results found. Please adjust your filters.</Box>
              )}
              {testSeriesObject.testSeriesStatus === "DRAFT" && (
                <Grid container justify="flex-end">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showExcludedTests}
                        color="secondary"
                        onChange={() => {
                          setShowExcludedTests((prev) => !prev);
                        }}
                        name="checkedA"
                      />
                    }
                    label="Show excluded Tests"
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={6}>
              {testSeriesObject.testSeriesStatus !== "DRAFT" && includedRows.length > 0 && (
                <TestCasesParametersTable
                  ref={(ref) => {
                    if (ref) {
                      this.testCasesParametersTableRef = ref;
                    }
                  }}
                  cursor={cursorState}
                  testCase={this.testCasesById[includedRows[cursorState].id]}
                  testSeriesStatus={testSeriesObject.testSeriesStatus!}
                  row={includedRows[cursorState]}
                  open={isTestCaseTableOpen}
                  onClose={() => {
                    this.testCasesParametersTableRef = null;
                    this.props.refreshTestCases();
                    this.setState({
                      isTestCaseTableOpen: false,
                    });
                  }}
                  handleLeftButtonClick={this.handleLeftButtonClick}
                  handleRightButtonClick={this.handleRightButtonClick}
                  readOnly={executionReadOnly}
                  headerArray={headerStateArray}
                  testSeriesObject={testSeriesObject}
                />
              )}
            </Grid>
          </Grid>
          <TestCasesNotesAddDialog
            header={this.renderMultiCaseHeader("NOTE")}
            testCaseIds={testCases
              .filter((testCase) => caseIndexesWithAttachment.includes(testCase.indexNumber))
              .map((testCase) => testCase.id)}
            onClose={this.handleOpenNotesAddDialog}
            open={isNotesAddDialogOpen}
            testSeriesId={testSeriesObject.id!}
          />

          <AttachmentDialog
            open={isAttachmentsAddDialogOpen ? { flag: OTHER } : null}
            header={this.renderMultiCaseHeader("ATTACHMENT")}
            onClose={this.handleOpenAttachmentsAddDialog}
            onSubmit={(filesForUpload, description) => {
              const { highlyConfidentialRegex } = this.state;

              const performUpload = () => {
                const testCaseIDs = this.state.rows
                  .filter((row: RowData) => {
                    return this.state.caseIndexesWithAttachment.includes(row.indexNumber);
                  })
                  .map((includedTestCase: RowData) => includedTestCase.id);
                const uploadHelper = new UploadHelper(this.attachmentApi);
                // Joining separate testCaseIndexes with the same attachment to prefix
                const filePrefix = `${testRequestId}_${testSeriesObject.indexNumber}_${caseIndexesWithAttachment.join(
                  "-",
                )}`;

                uploadHelper.upload(
                  filesForUpload.map((file) => {
                    const isImage = file.type.startsWith("image/");
                    const newName = `${filePrefix}_${isImage ? "Picture" : "Document"}`;
                    const flag = isImage ? PICTURE : DOCUMENT;
                    const name = FileNameHelper.renameFileWithEnding(newName, file.name);

                    return {
                      file,
                      description,
                      name,
                      flag,
                      notifications: {
                        finished: `Uploaded file ${name}.`,
                        error: `Failed to upload file ${name}.`,
                      },
                    };
                  }),
                  (attachment) => {
                    const isPicture = attachment.tags.includes("picture");
                    return this.testSeriesApi
                      .addAttachmentsToTestCase(testSeriesObject.id!, {
                        attachments: [
                          {
                            id: attachment.uuid,
                            type: isPicture ? "PICTURE" : "DOCUMENT",
                          },
                        ],
                        testCaseIds: testCaseIDs as [number, ...number[]],
                      })
                      .then(() => this.props.fetchTestSeriesApiData());
                  },
                  showSnackbar,
                );
              };

              // Check if any filename or description contains highly confidential info
              if (highlyConfidentialRegex) {
                const fileNames = filesForUpload.map((file) => file.name);
                const fieldsToCheck = [...fileNames, description || ""];
                const containsConfidentialInfo = containsHighlyConfidentialInfo(fieldsToCheck, highlyConfidentialRegex);

                if (containsConfidentialInfo) {
                  // Show confirmation dialog and store upload function for later
                  this.setState({
                    showConfidentialityDialog: true,
                    pendingAttachmentUpload: performUpload,
                  });
                  return;
                }
              }

              // If no confidential info detected, proceed with upload
              performUpload();
            }}
            onAlert={showErrorSnackbar}
          />
          <TestCasesNotesDetailDialog
            onClose={this.handleOpenNotesDetailDialog}
            open={isNotesDetailDialogOpen}
            testSeriesId={testSeriesObject.id!}
            testSeriesIndex={testSeriesObject.indexNumber!}
            selectedTestCaseIndex={
              testCases.filter((testCase) => {
                return testCase.id === selectedTestCaseId;
              })[0]?.indexNumber
            }
            selectedTestCaseId={selectedTestCaseId}
          />
          <AttachmentDetailDialog
            open={isAttachmentsDetailDialogOpen}
            disabled={!testSeriesPermissions.editTestCaseAttachmentsAndNotes}
            onClose={this.handleOpenAttachmentsDetailDialog}
            showIndex
            fileHandler={this.attachmentFileHandler}
            namingStrategy={namingStrategy}
            images={this.state.images}
            onDelete={(testCaseId: string) => {
              const { fetchTestSeriesApiData } = this.props;

              this.setState((prevState) => {
                const newImgArr = prevState.images.filter((img) => img.id !== testCaseId);
                if (newImgArr.length === 0) fetchTestSeriesApiData();
                return {
                  images: newImgArr,
                };
              });
            }}
            onDescriptionChange={() => {
              this.updateTable();
            }}
          />
          <div>
            <TestSeriesRegainMenu
              anchor={anchorEl}
              handleClose={this.handleClose}
              substrateSectionCount={testSeriesObject.substrateSections?.length || 0}
              testSeriesId={testSeriesObject.id!}
            />
          </div>
          <ConfidentialConfirmationDialog
            open={this.state.showConfidentialityDialog}
            objectName="attachment"
            onCancel={() => {
              this.setState({
                showConfidentialityDialog: false,
                pendingAttachmentUpload: null,
              });
            }}
            onProceed={() => {
              const { pendingAttachmentUpload } = this.state;

              this.testRequestApi
                .updateConfidentiality(testRequestId, "HIGHLY_CONFIDENTIAL")
                .then(() => {
                  showSnackbar("Test request marked as highly confidential");
                  if (pendingAttachmentUpload) {
                    pendingAttachmentUpload();
                  }
                })
                .catch(() => {
                  showErrorSnackbar("Failed to update test request confidentiality");
                })
                .finally(() => {
                  this.setState({
                    showConfidentialityDialog: false,
                    pendingAttachmentUpload: null,
                  });
                });
            }}
          />
        </>
      );
    }
  },
);

export default React.forwardRef<TestCasesOverviewTableRef, TestCasesOverviewTableProps>((props, ref) => (
  <ConfirmationDialogContext.Consumer>
    {(confirmationDialogContext) => (
      <TestSeriesDialogContext.Consumer>
        {(testSeriesDialogContext) => (
          <TestSeriesContext.Consumer>
            {(testSeriesContext) => (
              <ErrorSnackbarContext.Consumer>
                {(errorSnackbarContext) => (
                  <TestCasesOverviewTable
                    innerRef={ref}
                    testSeriesPermissions={testSeriesContext.permissions}
                    {...props}
                    {...confirmationDialogContext}
                    {...testSeriesDialogContext}
                    {...errorSnackbarContext}
                  />
                )}
              </ErrorSnackbarContext.Consumer>
            )}
          </TestSeriesContext.Consumer>
        )}
      </TestSeriesDialogContext.Consumer>
    )}
  </ConfirmationDialogContext.Consumer>
));
